// Audience panel — the pure DISPLAY layer. No React, no I/O.
//
// The aggregation rules live in src/lib/reporting/demographics.ts (shared with
// the client portal when it picks this up). What lives here is presentation the
// staff page owns: which dimensions to show and in what order, how a dimension
// and its values are named, and how a suppressed share renders.
//
// ⚠️ It is a separate module from the component for the reason the rest of this
// dashboard already splits them: a rendering rule worth pinning ("a suppressed
// share is a dash, never 0%") is pinned here, in a pure test, rather than in a
// jsdom render test this suite cannot comfortably afford.

import type { ReportingDemographicsDimension } from '../../types/reporting';

/**
 * The cuts, in display order.
 *
 * ⚠️ Ordered by what an AM actually asks, not by what the table stores: WHO
 * responded (age, gender) before WHERE (region) before HOW (platform, device).
 * `region` sits in the middle because it is the one an AM acts on — it maps to a
 * targeting change — while platform/device are diagnostic.
 */
export const DEMOGRAPHICS_DISPLAY_ORDER = [
  'age',
  'gender',
  'region',
  'publisher_platform',
  'device_platform',
] as const;

const DIMENSION_LABELS: Record<string, string> = {
  age: 'Age',
  gender: 'Gender',
  region: 'Region',
  publisher_platform: 'Platform',
  device_platform: 'Device',
};

/**
 * ⚠️ An UNKNOWN dimension is title-cased from its own key rather than dropped or
 * relabelled. Meta can add a breakdown; showing it under its raw name is honest,
 * and hiding it would make new data invisible with nothing to say so.
 */
export function dimensionLabel(dimension: string): string {
  return DIMENSION_LABELS[dimension] ?? titleCase(dimension.replace(/_/g, ' '));
}

function titleCase(s: string): string {
  return s
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Meta hands back lowercase enum-ish values ('female', 'audience_network') for
 * some dimensions and human strings for others ('Western Australia', '25-34').
 *
 * ⚠️ Age brackets pass through UNTOUCHED — '25-34' is already the label, and
 * title-casing it would achieve nothing but risk mangling it. Region likewise:
 * it already arrives as a place name.
 */
export function dimensionValueLabel(dimension: string, value: string): string {
  if (dimension === 'age' || dimension === 'region') return value;
  if (!/^[a-z0-9_]+$/.test(value)) return value;
  return titleCase(value.replace(/_/g, ' '));
}

/**
 * ⚠️ AN UNMEASURED SHARE IS A DASH, NEVER 0%. "0%" is a measured claim — it says
 * we looked and nobody responded — so a null (a zero denominator, i.e. division
 * by zero) must never render as a figure.
 *
 * ⚠️⚠️ AND THE MIRROR OF THAT RULE, WHICH THE FLOORS WERE HIDING: A NON-ZERO
 * SHARE MUST NEVER RENDER AS "0.0%". At one decimal place a real 0.04% rounds to
 * "0.0%" — indistinguishable from a measured zero, which is precisely the
 * confusion the rule above exists to prevent. Until 2026-08-31 the reliability
 * floors withheld every share small enough to hit it, so the case could not
 * arise; removing the floors is what exposed it.
 *
 * So a non-zero share below 0.05% renders "<0.1%", and "0.0%" is reserved for an
 * EXACT zero. ⚠️ Note the threshold is 0.05% and not 0.1%: 0.06% legitimately
 * rounds UP to "0.1%", and calling that "<0.1%" would be false.
 *
 * ⚠️ A NEGATIVE share is not expressible here (both numerator and denominator are
 * counts), so no sign handling is needed — and if one ever appears, `toFixed`
 * printing "-0.0%" is a louder signal than a silent "<0.1%".
 */
export function formatShare(share: number | null): string {
  if (share === null) return '—';
  if (share !== 0 && Math.abs(share) * 100 < 0.05) return '<0.1%';
  return `${(share * 100).toFixed(1)}%`;
}

/** Plain integer formatting; an absent count is a dash, not a zero. */
export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('en-US');
}

/**
 * Dimensions worth rendering, in display order.
 *
 * A dimension with no rows at all has nothing to say and is dropped. ⚠️ That is
 * now the ONLY reason one disappears: since nothing is folded, a dimension with
 * a single one-lead cell still renders that cell.
 *
 * ⚠️ An unrecognised dimension is APPENDED, never dropped — the same posture as
 * `dimensionLabel`. The stored order (which follows the pull's breakdown list) is
 * deliberately not the display order; see DEMOGRAPHICS_DISPLAY_ORDER.
 */
export function displayableDimensions(
  dimensions: ReportingDemographicsDimension[],
): ReportingDemographicsDimension[] {
  const rank = (d: string): number => {
    const i = DEMOGRAPHICS_DISPLAY_ORDER.indexOf(d as (typeof DEMOGRAPHICS_DISPLAY_ORDER)[number]);
    return i === -1 ? DEMOGRAPHICS_DISPLAY_ORDER.length : i;
  };
  return dimensions
    .filter((d) => d.rows.length > 0)
    .sort((a, b) => rank(a.dimension) - rank(b.dimension));
}

/**
 * ⚠️ THE SNAPSHOT CAVEAT, RENDERED UNCONDITIONALLY — including when we do not
 * know. Demographics only advance when the demographics pass runs, and at the
 * time of writing that pass is on NO SCHEDULE at all: it moves when someone
 * fires it by hand. An absent caveat reads as live, and a stale audience panel
 * that looks live is worse than no audience panel.
 *
 * ⚠️ IT NO LONGER OPENS "audience data" (2026-09-01). BOTH surfaces render it
 * immediately after a line that already begins "**Audience data** …" — the
 * client report now on the same ROW — so the prefix said it twice in one
 * breath. Only the prefix went; the caveat itself, and the unconditional
 * rendering, are unchanged.
 */
export function demographicsAsOfNote(
  asOf: string | null,
  formatInstant: (iso: string) => string,
): string {
  if (!asOf) {
    return "Snapshot from the last demographics pull, and we couldn't read when that was.";
  }
  return `Snapshot, last pulled ${formatInstant(asOf)}.`;
}
